/**
 * SchoolAdmissionsApply — public admissions form. Parents land here from the
 * shareable QR `/s/<slug>?redirect=/school/<tid>/admissions/apply`. No login
 * required, but logged-in users can still submit.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, GraduationCap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolAdmissionsApply() {
  const { tenantId } = useParams();
  const [form, setForm] = useState({
    first_name: '', last_name: '', date_of_birth: '', gender: 'male',
    previous_school: '', applying_for_class: '',
    guardian_name: '', guardian_phone: '', guardian_email: '',
    guardian_relationship: 'parent', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/school/public/tenants/${tenantId}/admissions/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Submission failed');
      setConfirmation(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-lg w-full" data-testid="admissions-confirmation">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="text-xl font-bold">Application received</h2>
            <p className="text-sm text-slate-600">
              Thanks for applying to <strong>{confirmation.school_name}</strong>.
              Your reference number is:
            </p>
            <code className="block text-lg font-mono bg-slate-100 px-3 py-2 rounded" data-testid="application-number">
              {confirmation.application_number}
            </code>
            <p className="text-xs text-slate-500">
              The school admissions team will review your application and contact you on
              the email/phone provided. Save this reference number for your records.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <Card className="max-w-2xl mx-auto" data-testid="admissions-apply-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-indigo-600" /> Admissions Application
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">All fields with * are required. Takes ~3 minutes.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>First name *</Label><Input required value={form.first_name} onChange={(e) => update('first_name', e.target.value)} data-testid="adm-first-name" /></div>
              <div><Label>Last name *</Label><Input required value={form.last_name} onChange={(e) => update('last_name', e.target.value)} data-testid="adm-last-name" /></div>
              <div><Label>Date of birth *</Label><Input type="date" required value={form.date_of_birth} onChange={(e) => update('date_of_birth', e.target.value)} data-testid="adm-dob" /></div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => update('gender', v)}>
                  <SelectTrigger data-testid="adm-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Previous school</Label><Input value={form.previous_school} onChange={(e) => update('previous_school', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>Applying for class/grade *</Label><Input required value={form.applying_for_class} onChange={(e) => update('applying_for_class', e.target.value)} placeholder="e.g. Year 3" data-testid="adm-class" /></div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <h3 className="font-medium text-sm text-slate-700">Parent / Guardian</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Full name *</Label><Input required value={form.guardian_name} onChange={(e) => update('guardian_name', e.target.value)} data-testid="adm-guardian-name" /></div>
                <div>
                  <Label>Relationship</Label>
                  <Select value={form.guardian_relationship} onValueChange={(v) => update('guardian_relationship', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="guardian">Guardian</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Phone *</Label><Input required value={form.guardian_phone} onChange={(e) => update('guardian_phone', e.target.value)} data-testid="adm-guardian-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={form.guardian_email} onChange={(e) => update('guardian_email', e.target.value)} data-testid="adm-guardian-email" /></div>
              </div>
            </div>

            <div>
              <Label>Anything else we should know?</Label>
              <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} rows={3} data-testid="adm-notes" />
            </div>

            <Button type="submit" className="w-full" disabled={submitting} data-testid="adm-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Submit application
            </Button>
            <p className="text-[11px] text-slate-400 text-center">By submitting you agree to be contacted by the school regarding this application.</p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
